/** Modifier classes for split start/end calendar panels (see component template). */
export const DUAL_PANEL_CALENDAR_START_PANEL_CLASS =
	'picker-panel__calendar-month--start';
export const DUAL_PANEL_CALENDAR_END_PANEL_CLASS =
	'picker-panel__calendar-month--end';

const HIDDEN_OPPOSITE_ENDPOINT_CELL =
	'pointer-events: none !important;';

export interface DualPanelCalendarHighlightStyleOptions {
	/** CSS selector prefix, e.g. `.cdk-overlay-pane:has(.custom-ckd-container) .picker-panel`. */
	selectorPrefix: string;
	accentColor: string;
	accentColorLight: string;
}

/**
 * Injected overlay styles for dual-calendar range highlighting.
 * Single source of truth — do not duplicate in component SCSS.
 */
export function buildDualPanelCalendarHighlightStyles({
	selectorPrefix,
	accentColor,
	accentColorLight,
}: DualPanelCalendarHighlightStyleOptions): string {
	const p = selectorPrefix;

	return `
      ${p} .mat-calendar-body-cell-content.mat-calendar-body-selected {
        background: var(--picker-accent-color, ${accentColor}) !important;
        color: #fff !important;
      }

      ${p} .mat-calendar-body-in-range::before {
        background: ${accentColorLight} !important;
      }

      ${p} .mat-calendar-body-in-range > .mat-calendar-body-cell-content:not(.mat-calendar-body-selected) {
        background: transparent !important;
      }

      ${p} .${DUAL_PANEL_CALENDAR_START_PANEL_CLASS} .mat-calendar-body-cell.mat-calendar-body-range-end:not(.mat-calendar-body-range-start) {
        background-color: transparent !important;
        ${HIDDEN_OPPOSITE_ENDPOINT_CELL}
      }
      ${p} .${DUAL_PANEL_CALENDAR_START_PANEL_CLASS} .mat-calendar-body-cell.mat-calendar-body-range-end:not(.mat-calendar-body-range-start)::before {
        background: ${accentColorLight} !important;
      }
      ${p} .${DUAL_PANEL_CALENDAR_START_PANEL_CLASS} .mat-calendar-body-cell.mat-calendar-body-range-end:not(.mat-calendar-body-range-start) .mat-calendar-body-cell-content.mat-calendar-body-selected {
        visibility: hidden;
        background: transparent !important;
        color: transparent !important;
        box-shadow: none !important;
      }

      ${p} .${DUAL_PANEL_CALENDAR_END_PANEL_CLASS} .mat-calendar-body-cell.mat-calendar-body-range-start:not(.mat-calendar-body-range-end) {
        background-color: transparent !important;
        ${HIDDEN_OPPOSITE_ENDPOINT_CELL}
      }
      ${p} .${DUAL_PANEL_CALENDAR_END_PANEL_CLASS} .mat-calendar-body-cell.mat-calendar-body-range-start:not(.mat-calendar-body-range-end)::before {
        background: ${accentColorLight} !important;
      }
      ${p} .${DUAL_PANEL_CALENDAR_END_PANEL_CLASS} .mat-calendar-body-cell.mat-calendar-body-range-start:not(.mat-calendar-body-range-end) .mat-calendar-body-cell-content.mat-calendar-body-selected {
        visibility: hidden;
        background: transparent !important;
        color: transparent !important;
        box-shadow: none !important;
      }
    `;
}

const OPPOSITE_ENDPOINT_ON_START_PANEL_SELECTOR =
	`.${DUAL_PANEL_CALENDAR_START_PANEL_CLASS} .mat-calendar-body-cell.mat-calendar-body-range-end:not(.mat-calendar-body-range-start)`;
const OPPOSITE_ENDPOINT_ON_END_PANEL_SELECTOR =
	`.${DUAL_PANEL_CALENDAR_END_PANEL_CLASS} .mat-calendar-body-cell.mat-calendar-body-range-start:not(.mat-calendar-body-range-end)`;

/**
 * Hides the opposite endpoint from assistive tech while keeping the in-range cap visible.
 * Call after each calendar render (cells are recreated on month/range changes).
 */
export function applyDualPanelCalendarA11y(panelRoot: ParentNode): void {
	resetDualPanelCalendarA11y(panelRoot);

	panelRoot
		.querySelectorAll<HTMLButtonElement>(OPPOSITE_ENDPOINT_ON_START_PANEL_SELECTOR)
		.forEach(cell => {
			cell.setAttribute('aria-hidden', 'true');
			cell.setAttribute('tabindex', '-1');
			cell.removeAttribute('aria-pressed');
			cell
				.querySelector('.mat-calendar-body-cell-content')
				?.setAttribute('aria-hidden', 'true');
		});

	panelRoot
		.querySelectorAll<HTMLButtonElement>(OPPOSITE_ENDPOINT_ON_END_PANEL_SELECTOR)
		.forEach(cell => {
			cell.setAttribute('aria-hidden', 'true');
			cell.setAttribute('tabindex', '-1');
			cell.removeAttribute('aria-pressed');
			cell
				.querySelector('.mat-calendar-body-cell-content')
				?.setAttribute('aria-hidden', 'true');
		});
}

/** Clears a11y overrides before re-applying (idempotent across re-renders). */
export function resetDualPanelCalendarA11y(panelRoot: ParentNode): void {
	panelRoot
		.querySelectorAll<HTMLButtonElement>(
			`${OPPOSITE_ENDPOINT_ON_START_PANEL_SELECTOR}, ${OPPOSITE_ENDPOINT_ON_END_PANEL_SELECTOR}`
		)
		.forEach(cell => {
			cell.removeAttribute('aria-hidden');
			cell.removeAttribute('tabindex');
			cell
				.querySelector('.mat-calendar-body-cell-content')
				?.removeAttribute('aria-hidden');
		});
}
